import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { uid } from "../mock";
import {
  apiListQuotes,
  apiCreateQuote,
  apiUpdateQuote,
  apiDeleteQuote,
} from "../api";

const QuotesContext = createContext(null);

export const buildDefaultQuote = () => ({
  id: uid(),
  name: "Cotización nueva",
  createdAt: new Date().toISOString().slice(0, 10),
  brandTitle: "UAICARD MX",
  brandSubtitle:
    "Perfiles de venta, invitaciones digitales, credenciales, tarjetas NFC, Plataforma escolar UAICARD ACCESS.",
  heading: "Cotización",
  descriptionTitle: "DESCRIPCIÓN:",
  descriptionBody:
    "CREDENCIAL PVC CON QR: Diseño personalizado, impresión holográfica premium, QR programado y personalizado.",
  serviceHeader: "SERVICIO",
  totalHeader: "TOTAL",
  items: [
    { id: uid(), service: "credencial PVC + QR", total: "$70 MXN / 500 pz. $35,000 MXN" },
    { id: uid(), service: "credencial PVC + QR + protector rígido.", total: "$100 MXN / 500 pz. $50,000 MXN" },
    { id: uid(), service: "credencial PVC + QR + protector rígido + lanyard personalizado.", total: "$125 MXN / 500 pz. $60,000 MXN" },
  ],
  subtotalLabel: "Sub-total:",
  subtotal: "N/A",
  totalLabel: "TOTAL:",
  total: "N/A",
  clientLabel: "CLIENTE:",
  clientName: "COLEGIO MEY",
  deliveryNote: "TODO TRABAJO SE ENTREGARÁ DE 5 a 15 DÍAS HÁBILES.",
  paymentLabel: "PAGO:",
  paymentMethod: "Transferencia bancaria",
  accountInfo: "Cuenta Banamex:\n0021 8070 2313 440298\nYOSTHON MORENO SALAS",
  footerNote:
    "Envía tu comprobante por WhatsApp al 55 6768 7979 para iniciar tu pedido y realizar la visita a tu negocio o domicilio.",
  footerNote2: "En caso de requerir factura, los costos son más IVA.",
  whatsapp: "525567687979",
});

export const QuotesProvider = ({ children }) => {
  const [quotes, setQuotes] = useState([]);
  const [ready, setReady] = useState(false);
  const saveTimers = useRef({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let list = await apiListQuotes();
        if (!list || list.length === 0) {
          const seed = buildDefaultQuote();
          seed.name = "Credenciales Colegio MEY";
          await apiCreateQuote(seed);
          list = [seed];
        }
        if (mounted) setQuotes(list);
      } catch (e) {
        console.error("Error loading quotes", e);
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const getQuote = (id) => quotes.find((q) => q.id === id);

  const scheduleSave = (quote) => {
    const id = quote.id;
    if (saveTimers.current[id]) clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(() => {
      apiUpdateQuote(id, quote).catch((e) => console.error("save quote error", e));
    }, 500);
  };

  const createQuote = () => {
    const q = buildDefaultQuote();
    setQuotes((prev) => [q, ...prev]);
    apiCreateQuote(q).catch((e) => console.error("create quote error", e));
    return q;
  };

  const updateQuote = (id, updater) => {
    setQuotes((prev) => {
      const next = prev.map((q) =>
        q.id === id ? (typeof updater === "function" ? updater(q) : { ...q, ...updater }) : q
      );
      const updated = next.find((q) => q.id === id);
      if (updated) scheduleSave(updated);
      return next;
    });
  };

  const duplicateQuote = (id) => {
    const src = quotes.find((q) => q.id === id);
    if (!src) return null;
    const copy = { ...JSON.parse(JSON.stringify(src)), id: uid(), name: `${src.name} (copia)`, createdAt: new Date().toISOString().slice(0, 10) };
    delete copy._created;
    setQuotes((prev) => [copy, ...prev]);
    apiCreateQuote(copy).catch((e) => console.error("dup quote error", e));
    return copy;
  };

  const deleteQuote = (id) => {
    setQuotes((prev) => prev.filter((q) => q.id !== id));
    apiDeleteQuote(id).catch((e) => console.error("delete quote error", e));
  };

  return (
    <QuotesContext.Provider
      value={{ quotes, ready, getQuote, createQuote, updateQuote, duplicateQuote, deleteQuote }}
    >
      {children}
    </QuotesContext.Provider>
  );
};

export const useQuotes = () => {
  const ctx = useContext(QuotesContext);
  if (!ctx) throw new Error("useQuotes must be used within QuotesProvider");
  return ctx;
};
